import { AnyAction, createSlice, PayloadAction, Reducer } from '@reduxjs/toolkit';
import React from 'react';
import useIsMounted from 'Hooks/useIsMounted';

interface IState<D extends object> {
    isOpen: boolean;
    initialFormState: D;
    rowId: string | null;
}

const splitViewState = {
    isOpen: false,
    initialFormState: {},
    rowId: null
} as IState<any>;

const splitViewSlice = createSlice({
    name: 'splitview',
    initialState: splitViewState,
    reducers: {
        updateFormOpen: (state, action: PayloadAction<boolean>) => {
            state.isOpen = action.payload;
        },
        updateInitialFormState: (state, action: PayloadAction<any>) => {
            state.initialFormState = action.payload;
        },
        updateRowId: (state, action: PayloadAction<any>) => {
            state.rowId = action.payload;
        }
    }
});

const { updateFormOpen, updateInitialFormState, updateRowId } = splitViewSlice.actions;
const { reducer } = splitViewSlice;

export interface UseSplitViewForm<D extends object> {
    splitViewFormState: IState<D>;
    setIsOpen: (v: boolean) => void;
    setInitialFormState: (formState: IState<D>['initialFormState']) => void;
    setRowId: (v: any) => void;
}

function useSplitViewForm<D extends object>(initialState: IState<D> = splitViewState): UseSplitViewForm<D> {
    const isMounted = useIsMounted();
    const [state, dispatch] = React.useReducer(reducer as Reducer<IState<D>, AnyAction>, initialState);
    const safeDispatch = React.useCallback(
        (arg) => {
            return isMounted ? dispatch(arg) : () => {};
        },
        [isMounted]
    );

    const setIsOpen = React.useCallback(
        (v: boolean) => {
            safeDispatch(updateFormOpen(v));
        },
        [safeDispatch]
    );

    const setInitialFormState = React.useCallback(
        (formState: IState<D>['initialFormState']) => {
            safeDispatch(updateInitialFormState(formState));
        },
        [safeDispatch]
    );

    const setRowId = React.useCallback(
        (v: any) => {
            safeDispatch(updateRowId(v));
        },
        [safeDispatch]
    );

    return {
        splitViewFormState: state,
        setIsOpen,
        setInitialFormState,
        setRowId
    };
}

export default useSplitViewForm;
